import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  Tooltip as RechartsTooltip, CartesianGrid, ReferenceLine, Customized,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import api from '../utils/axiosConfig';
import { filterOutliersWithFallback } from '../utils/outlierFilter';
import { normalizeYmd, placeRacesOnChartCategories } from '../utils/raceStripChart';
import { cn } from '../lib/utils';
import { AcwrRaceStripLayer } from './charts/AcwrRaceStripLayer';
import { RaceStripHoverCard } from './charts/RaceStripHoverCard';
import ActivityPeekModal from './ActivityPeekModal';

/** Fixed chart pixel height — avoids layout reflow when selections change (Recharts measures width only). */
const CHART_FIXED_HEIGHT = 400;

const KPI_DEFS = [
  { key: 're', color: '#10b981', labelKey: 'activityDetail.kpi.re', unit: 'kg/N', decimals: 2, higherIsBetter: true, components: ['pace', 'watts'] },
  { key: 'stride', color: '#f59e0b', labelKey: 'activityDetail.kpi.stride', unit: 'm', decimals: 2, higherIsBetter: true, components: ['cadence', 'distance'] },
  { key: 'paceBpm', color: '#3b82f6', labelKey: 'activityDetail.kpi.paceBpm', unit: 's·km⁻¹/bpm', decimals: 2, higherIsBetter: false, components: ['pace', 'hr'] },
  { key: 'bpmWatt', color: '#ef4444', labelKey: 'activityDetail.kpi.bpmWatt', unit: 'bpm/W', decimals: 2, higherIsBetter: false, components: ['hr', 'watts'] },
  { key: 'paceWatt', color: '#a855f7', labelKey: 'activityDetail.kpi.paceWatt', unit: 's·km⁻¹/W', decimals: 2, higherIsBetter: false, components: ['pace', 'watts'] },
  { key: 'elevKm', color: '#6366f1', labelKey: 'activityDetail.kpi.elevKm', unit: 'm/km', decimals: 1, higherIsBetter: false, components: ['elevGain', 'distance'] },
  { key: 'calKm', color: '#f97316', labelKey: 'activityDetail.kpi.calKm', unit: 'kcal/km', decimals: 0, higherIsBetter: false, components: ['distance'] },
];

const RANGES = [
  { key: '7d', labelKey: 'kpiTrend.7d' },
  { key: '14d', labelKey: 'kpiTrend.14d' },
  { key: '21d', labelKey: 'kpiTrend.21d' },
  { key: '28d', labelKey: 'kpiTrend.28d' },
  { key: '1w', labelKey: 'kpiTrend.1w' },
  { key: '1m', labelKey: 'kpiTrend.1m' },
  { key: '3m', labelKey: 'kpiTrend.3m' },
  { key: '6m', labelKey: 'kpiTrend.6m' },
  { key: 'ytd', labelKey: 'kpiTrend.ytd' },
  { key: '1y', labelKey: 'kpiTrend.1y' },
  { key: '2y', labelKey: 'kpiTrend.2y' },
  { key: '3y', labelKey: 'kpiTrend.3y' },
  { key: 'all', labelKey: 'kpiTrend.all' },
];

const GRADE_SEGMENTS = [
  { key: 'all', labelKey: 'kpiTrend.allGrades' },
  { key: 'low', labelKey: 'kpiTrend.gradeLow', min: 0, max: 1 },
  { key: 'medium', labelKey: 'kpiTrend.gradeMedium', min: 1, max: 3 },
  { key: 'high', labelKey: 'kpiTrend.gradeHigh', min: 3, max: Infinity },
];

const PACE_SEGMENTS = [
  { key: 'all', label: 'All', min: 0, max: Infinity },
  { key: '230-300', label: '2:30–3:00', min: 150, max: 180 },
  { key: '300-330', label: '3:00–3:30', min: 180, max: 210 },
  { key: '330-400', label: '3:30–4:00', min: 210, max: 240 },
  { key: '400-430', label: '4:00–4:30', min: 240, max: 270 },
  { key: '430-500', label: '4:30–5:00', min: 270, max: 300 },
  { key: '500-530', label: '5:00–5:30', min: 300, max: 330 },
  { key: '530-600', label: '5:30–6:00', min: 330, max: 360 },
  { key: '600-700', label: '6:00–7:00', min: 360, max: 420 },
  { key: '700+', label: '7:00+', min: 420, max: Infinity },
];

function paceSecondsFromSpeed(avgSpeed) {
  if (!avgSpeed || avgSpeed <= 0) return Infinity;
  return 1000 / avgSpeed;
}

/** Shift = toggle; non-shift = only this key. "all" clears other bands; shift+"all" resets to all only. */
function applyFilterClick(prev, key, shiftKey) {
  if (!shiftKey) {
    return [key];
  }
  if (key === 'all') {
    return ['all'];
  }
  const withoutAll = prev.filter((k) => k !== 'all');
  if (withoutAll.includes(key)) {
    const next = withoutAll.filter((k) => k !== key);
    return next.length === 0 ? ['all'] : next;
  }
  return [...withoutAll, key];
}

function lapMatchesPaceBands(d, keys) {
  if (!keys?.length || keys.includes('all')) return true;
  const ps = paceSecondsFromSpeed(d.avgSpeed);
  return keys.some((k) => {
    const seg = PACE_SEGMENTS.find((p) => p.key === k);
    return seg && ps >= seg.min && ps < seg.max;
  });
}

function lapMatchesGradeBands(d, keys) {
  if (!keys?.length || keys.includes('all')) return true;
  const dist = d.distance || 0;
  const elev = d.elevGain ?? 0;
  const gradePct = dist > 0 ? (elev / dist) * 100 : 0;
  return keys.some((k) => {
    const seg = GRADE_SEGMENTS.find((g) => g.key === k);
    if (!seg) return false;
    return gradePct >= seg.min && gradePct < seg.max;
  });
}

/** Same logic as KPI averages list: % change vs previous period (not regression). */
function getPeriodTrendInfo(currentVal, priorVal, def) {
  if (currentVal == null || priorVal == null || Math.abs(priorVal) < 1e-6) return null;
  const pctChange = ((currentVal - priorVal) / Math.abs(priorVal)) * 100;
  const isImprovement = def.higherIsBetter ? pctChange > 0 : pctChange < 0;
  const isNeutral = Math.abs(pctChange) < 0.5;
  return { pctChange, isImprovement, isNeutral };
}

function computeTrend(points, key) {
  if (points.length < 2) return { slope: 0, intercept: 0, startVal: 0, endVal: 0, pctChange: 0, data: points };

  const n = points.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += points[i][key];
    sumXY += i * points[i][key];
    sumX2 += i * i;
  }
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const startVal = intercept;
  const endVal = intercept + slope * (n - 1);
  const pctChange = startVal !== 0 ? ((endVal - startVal) / Math.abs(startVal)) * 100 : 0;

  const enriched = points.map((p, i) => ({ ...p, _trend: parseFloat((intercept + slope * i).toFixed(4)) }));

  return { slope, intercept, startVal, endVal, pctChange, data: enriched };
}

function formatPace(avgSpeed) {
  if (!avgSpeed || avgSpeed <= 0) return null;
  const p = 1000 / avgSpeed;
  const m = Math.floor(p / 60);
  const s = Math.round(p % 60);
  return `${m}:${s.toString().padStart(2, '0')}/km`;
}

function formatElapsedSeconds(sec) {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function downsampleSortedUnique(arr, maxTicks) {
  if (arr.length <= maxTicks) return arr;
  const out = [];
  for (let i = 0; i < maxTicks; i++) {
    const idx = Math.round((i * (arr.length - 1)) / Math.max(1, maxTicks - 1));
    out.push(arr[idx]);
  }
  return [...new Set(out)];
}

function getComponentDetails(d, components) {
  const details = [];
  if (!components) return details;
  for (const c of components) {
    switch (c) {
      case 'pace': {
        const p = formatPace(d.avgSpeed);
        if (p) details.push({ label: 'Pace', value: p });
        break;
      }
      case 'hr':
        if (d.hr) details.push({ label: 'HR', value: `${Math.round(d.hr)} bpm` });
        break;
      case 'watts':
        if (d.watts) details.push({ label: 'Power', value: `${Math.round(d.watts)} W` });
        break;
      case 'cadence':
        if (d.cadence) details.push({ label: 'Cadence', value: `${Math.round(d.cadence * 2)} spm` });
        break;
      case 'elevGain':
        if (d.elevGain) details.push({ label: 'Elev', value: `${d.elevGain.toFixed(0)} m` });
        break;
      case 'distance':
        if (d.distance > 0) details.push({ label: 'Dist', value: `${(d.distance / 1000).toFixed(2)} km` });
        break;
      default: break;
    }
  }
  return details;
}

function CustomTooltip({ active, payload, label, kpiDef, t, isCurrentActivity, isDurationAxis }) {
  if (!active || !payload?.length) return null;
  const kpiItem = payload.find((p) => p?.dataKey === kpiDef.key);
  const d = kpiItem?.payload ?? payload[0]?.payload;
  if (!d) return null;

  const val = d[kpiDef.key];
  const date = d.date ? new Date(d.date) : null;
  const dateStr = date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const timeStr = isDurationAxis ? formatElapsedSeconds(d.elapsedSec) : dateStr;
  const details = getComponentDetails(d, kpiDef.components);
  if (d.distance > 0 && !kpiDef.components?.includes('distance')) {
    details.push({ label: 'Dist', value: `${(d.distance / 1000).toFixed(2)} km` });
  }

  return (
    <div className={`bg-popover border rounded-lg px-3 py-2 shadow-xl text-xs ${isCurrentActivity ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}>
      {isCurrentActivity && (
        <p className='text-[10px] font-semibold text-primary mb-1'>{t('kpiTrend.thisActivity')}</p>
      )}
      <p className='font-medium text-foreground mb-1'>{d.name}</p>
      {d.lapName && (
        <p className='text-muted-foreground text-[10px]'>{d.lapName}</p>
      )}
      {isDurationAxis ? (
        <p className='text-muted-foreground'>
          <span className='opacity-70'>{t('kpiTrend.elapsedInRun')}: </span>
          {timeStr}
        </p>
      ) : (
        <p className='text-muted-foreground'>{dateStr}</p>
      )}
      {isDurationAxis && d.altitudeM != null && Number.isFinite(Number(d.altitudeM)) && (
        <p className='text-muted-foreground text-[10px] mt-0.5'>
          <span className='opacity-70'>{t('athlete.elevation')}: </span>
          <span className='text-foreground font-medium'>{Math.round(Number(d.altitudeM))} m</span>
        </p>
      )}
      {val != null && (
        <p className='mt-1 font-bold text-sm' style={{ color: kpiDef.color }}>
          {val.toFixed(kpiDef.decimals)} {kpiDef.unit}
        </p>
      )}
      {details.length > 0 && (
        <div className='flex items-center gap-2.5 mt-1 text-muted-foreground'>
          {details.map((item, i) => (
            <span key={i}>
              <span className='opacity-60'>{item.label}</span>{' '}
              <span className='text-foreground font-medium'>{item.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomDot({ cx, cy, payload, kpiKey, color, isCurrentActivity, dotClickable, onDotActivityClick }) {
  if (payload[kpiKey] == null || !cx || !cy) return null;
  const r = isCurrentActivity ? 5 : 3;
  const rawAid = payload?.activityId;
  const aid = rawAid != null ? Number(rawAid) : NaN;
  const canClick = dotClickable && Number.isFinite(aid) && aid > 0 && typeof onDotActivityClick === 'function';

  const handleClick = (e) => {
    if (!canClick) return;
    e.stopPropagation();
    onDotActivityClick(aid);
  };

  return (
    <g
      style={{ cursor: canClick ? 'pointer' : 'default' }}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (!canClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onDotActivityClick(aid);
        }
      }}
      {...(canClick ? { role: 'button', tabIndex: 0 } : {})}
    >
      {isCurrentActivity && (
        <circle cx={cx} cy={cy} r={8} fill={color} fillOpacity={0.2} stroke='none' />
      )}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={color}
        stroke={isCurrentActivity ? 'hsl(var(--foreground))' : 'none'}
        strokeWidth={isCurrentActivity ? 2 : 0}
        fillOpacity={0.9}
      />
    </g>
  );
}

function CustomActiveDot({ cx, cy, payload, kpiKey, color, isCurrentActivity, dotClickable, onDotActivityClick }) {
  if (payload[kpiKey] == null || !cx || !cy) return null;
  const r = isCurrentActivity ? 7 : 4;
  const rawAid = payload?.activityId;
  const aid = rawAid != null ? Number(rawAid) : NaN;
  const canClick = dotClickable && Number.isFinite(aid) && aid > 0 && typeof onDotActivityClick === 'function';

  const handleClick = (e) => {
    if (!canClick) return;
    e.stopPropagation();
    onDotActivityClick(aid);
  };

  return (
    <g
      style={{ cursor: canClick ? 'pointer' : 'default' }}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (!canClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onDotActivityClick(aid);
        }
      }}
      {...(canClick ? { role: 'button', tabIndex: 0 } : {})}
    >
      <circle cx={cx} cy={cy} r={r + 4} fill={color} fillOpacity={0.2} stroke='none' />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={color}
        stroke='hsl(var(--background))'
        strokeWidth={isCurrentActivity ? 3 : 2}
      />
    </g>
  );
}

const DEFAULT_PREFS = {
  range: '6m',
  activeKpi: 're',
  paceFilter: 'all',
  gradeFilter: 'all',
  paceFilters: ['all'],
  gradeFilters: ['all'],
};

const ROLLING_RANGE_KEYS = new Set(['7d', '14d', '21d', '28d']);

export default function KpiTrendModal({
  isOpen,
  onClose,
  initialKpi,
  currentActivityId,
  activityKpiFromCard,
  syncedRollingRange,
}) {
  const { t } = useLanguage();
  const [activeKpi, setActiveKpi] = useState(initialKpi || DEFAULT_PREFS.activeKpi);
  const [range, setRange] = useState(DEFAULT_PREFS.range);
  const [paceFilterKeys, setPaceFilterKeys] = useState(DEFAULT_PREFS.paceFilters);
  const [gradeFilterKeys, setGradeFilterKeys] = useState(DEFAULT_PREFS.gradeFilters);
  const [data, setData] = useState([]);
  const [runSeriesMeta, setRunSeriesMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  /** Bumps after layout so ResponsiveContainer measures non-zero size (portal/modal animation). */
  const [layoutNonce, setLayoutNonce] = useState(0);
  const overlayRef = useRef(null);
  const chartHostRef = useRef(null);
  /** Width only; height is fixed (CHART_FIXED_HEIGHT) so filter rows do not shift when chart state changes. */
  const [chartWidth, setChartWidth] = useState(0);
  const [peekActivityId, setPeekActivityId] = useState(null);
  const [raceRuns, setRaceRuns] = useState([]);
  const [showRaceStrip, setShowRaceStrip] = useState(false);
  const [raceTip, setRaceTip] = useState(null);
  const [periodAvgs, setPeriodAvgs] = useState({ current: {}, prior: {} });
  const [periodAvgsLoading, setPeriodAvgsLoading] = useState(false);
  const saveTimeoutRef = useRef(null);
  const pendingPrefsRef = useRef(null);
  const persistedRangeRef = useRef(DEFAULT_PREFS.range);
  const fetchGenRef = useRef(0);
  const showThisRun = currentActivityId != null && String(currentActivityId).trim() !== '';
  const isThisRun = showThisRun && range === 'thisRun';

  const flushPendingSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (pendingPrefsRef.current) {
      api.put('/api/users/settings', { kpiTrendPreferences: pendingPrefsRef.current }).catch(() => {});
      pendingPrefsRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (pendingPrefsRef.current) {
        api.put('/api/users/settings', { kpiTrendPreferences: pendingPrefsRef.current }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) setRunSeriesMeta(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setLayoutNonce(0);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setChartWidth(0);
      return;
    }
    const el = chartHostRef.current;
    if (!el) return;

    const measure = () => {
      const node = chartHostRef.current;
      if (!node) return;
      const w = Math.round(node.clientWidth);
      if (w >= 2) {
        setChartWidth((prev) => (prev === w ? prev : w));
      }
    };

    measure();
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    const delayed = window.setTimeout(measure, 200);

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      const w = Math.round(cr.width);
      if (w >= 2) {
        setChartWidth((prev) => (prev === w ? prev : w));
      }
    });
    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(delayed);
      ro.disconnect();
    };
  }, [isOpen, loading, data.length, layoutNonce, animKey, activeKpi, range]);

  useEffect(() => {
    if (!isOpen || loading || data.length === 0) return undefined;
    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      if (cancelled) return;
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        window.dispatchEvent(new Event('resize'));
        setLayoutNonce((n) => n + 1);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isOpen, loading, data.length, range]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    api.get('/api/users/settings')
      .then((res) => {
        if (cancelled) return;
        const p = res.data?.kpiTrendPreferences || res.data?.settings?.kpi_trend_preferences || {};
        if (p.range || p.paceFilter || p.gradeFilter || p.paceFilters || p.gradeFilters) {
          if (!showThisRun && p.range && !syncedRollingRange) setRange(p.range);
          if (Array.isArray(p.paceFilters) && p.paceFilters.length > 0) {
            setPaceFilterKeys(p.paceFilters);
          } else if (p.paceFilter) {
            setPaceFilterKeys([p.paceFilter]);
          }
          if (Array.isArray(p.gradeFilters) && p.gradeFilters.length > 0) {
            setGradeFilterKeys(p.gradeFilters);
          } else if (p.gradeFilter) {
            setGradeFilterKeys([p.gradeFilter]);
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, showThisRun, syncedRollingRange]);

  const prevOpenRef = useRef(false);
  // useLayoutEffect so range / activeKpi apply before useEffect(fetchTrends) and settings fetch.
  useLayoutEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (justOpened) {
      if (showThisRun) setRange('thisRun');
      else if (syncedRollingRange) setRange(syncedRollingRange);
      if (initialKpi) setActiveKpi(initialKpi);
    }
  }, [isOpen, showThisRun, initialKpi, syncedRollingRange]);

  const savePrefs = useCallback((updates) => {
    const next = {
      range,
      activeKpi,
      paceFilter: paceFilterKeys.length === 1 ? paceFilterKeys[0] : 'all',
      gradeFilter: gradeFilterKeys.length === 1 ? gradeFilterKeys[0] : 'all',
      paceFilters: paceFilterKeys,
      gradeFilters: gradeFilterKeys,
      ...updates,
    };
    const rangeForApi = next.range === 'thisRun'
      ? (persistedRangeRef.current || DEFAULT_PREFS.range)
      : next.range;
    pendingPrefsRef.current = { ...next, range: rangeForApi };
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      api.put('/api/users/settings', { kpiTrendPreferences: { ...next, range: rangeForApi } }).catch(() => {});
      saveTimeoutRef.current = null;
      pendingPrefsRef.current = null;
    }, 400);
  }, [range, activeKpi, paceFilterKeys, gradeFilterKeys]);

  useEffect(() => {
    if (range !== 'thisRun') persistedRangeRef.current = range;
  }, [range]);

  const updateRange = useCallback((r) => {
    setRange(r);
    savePrefs({ range: r });
  }, [savePrefs]);

  const updateActiveKpi = useCallback((k) => {
    setActiveKpi(k);
    setAnimKey((n) => n + 1);
    savePrefs({ activeKpi: k });
  }, [savePrefs]);

  const handlePaceFilterClick = useCallback((key, e) => {
    const next = applyFilterClick(paceFilterKeys, key, e.shiftKey);
    setPaceFilterKeys(next);
    setAnimKey((n) => n + 1);
    savePrefs({
      paceFilters: next,
      paceFilter: next.length === 1 ? next[0] : 'all',
    });
  }, [paceFilterKeys, savePrefs]);

  const handleGradeFilterClick = useCallback((key, e) => {
    const next = applyFilterClick(gradeFilterKeys, key, e.shiftKey);
    setGradeFilterKeys(next);
    setAnimKey((n) => n + 1);
    savePrefs({
      gradeFilters: next,
      gradeFilter: next.length === 1 ? next[0] : 'all',
    });
  }, [gradeFilterKeys, savePrefs]);

  const fetchTrends = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    setLoading(true);
    try {
      if (range === 'thisRun' && showThisRun) {
        const res = await api.get(
          `/api/strava/activity/${encodeURIComponent(currentActivityId)}/kpi-series?windowSeconds=30`
        );
        if (gen !== fetchGenRef.current) return;
        setRunSeriesMeta({
          durationSec: res.data.durationSec ?? 0,
          windowSeconds: res.data.windowSeconds ?? 30,
          missingStreams: !!res.data.missingStreams,
          streamsUnavailable: !!res.data.streamsUnavailable,
        });
        setData(res.data.samples || []);
      } else {
        setRunSeriesMeta(null);
        const r = range === 'thisRun' ? (persistedRangeRef.current || DEFAULT_PREFS.range) : range;
        const res = await api.get(`/api/strava/kpi-trends?range=${encodeURIComponent(r)}`);
        if (gen !== fetchGenRef.current) return;
        setData(res.data.data || []);
      }
      if (gen === fetchGenRef.current) setAnimKey((k) => k + 1);
    } catch {
      if (gen !== fetchGenRef.current) return;
      setData([]);
      setRunSeriesMeta(null);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [range, showThisRun, currentActivityId]);

  useEffect(() => {
    if (isOpen) fetchTrends();
  }, [isOpen, fetchTrends]);

  useEffect(() => {
    if (!isOpen || isThisRun || !ROLLING_RANGE_KEYS.has(range)) {
      setPeriodAvgs({ current: {}, prior: {} });
      setPeriodAvgsLoading(false);
      return;
    }
    const days = parseInt(String(range).replace('d', ''), 10);
    if (![7, 14, 21, 28].includes(days)) return;
    let cancelled = false;
    setPeriodAvgsLoading(true);
    const paceFilterForApi = paceFilterKeys.length === 1 ? paceFilterKeys[0] : 'all';
    api
      .get(`/api/strava/kpi-averages?days=${days}&paceFilter=${encodeURIComponent(paceFilterForApi)}`)
      .then((res) => {
        if (cancelled) return;
        setPeriodAvgs({
          current: res.data?.current || {},
          prior: res.data?.prior || {},
        });
      })
      .catch(() => {
        if (!cancelled) setPeriodAvgs({ current: {}, prior: {} });
      })
      .finally(() => {
        if (!cancelled) setPeriodAvgsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, range, paceFilterKeys, isThisRun]);

  const handleClose = useCallback(() => {
    flushPendingSave();
    onClose();
  }, [flushPendingSave, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setPeekActivityId(null);
      return;
    }
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (peekActivityId != null) {
        setPeekActivityId(null);
        return;
      }
      handleClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, handleClose, peekActivityId]);

  useEffect(() => {
    if (!isOpen) setRaceTip(null);
  }, [isOpen]);

  useEffect(() => {
    if (!showRaceStrip) setRaceTip(null);
  }, [showRaceStrip]);

  useEffect(() => {
    if (!isOpen || isThisRun || !(data || []).length) {
      setRaceRuns([]);
      return undefined;
    }
    const ymds = [...new Set(data.map((d) => normalizeYmd(d.date)).filter(Boolean))].sort();
    if (!ymds.length) {
      setRaceRuns([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(
        `/api/strava/race-runs-range?start=${encodeURIComponent(ymds[0])}&end=${encodeURIComponent(ymds[ymds.length - 1])}`
      )
      .then((res) => {
        if (!cancelled) setRaceRuns(res.data.races || []);
      })
      .catch(() => {
        if (!cancelled) setRaceRuns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isThisRun, data]);

  const periodDotClickable = !isThisRun;

  const kpiDef = KPI_DEFS.find(k => k.key === activeKpi) || KPI_DEFS[0];
  const gradeIsListOnlyAll = gradeFilterKeys.length === 1 && gradeFilterKeys[0] === 'all';
  const paceIsSingleForApi = paceFilterKeys.length === 1;

  const paceFilteredData = data.filter((d) => lapMatchesPaceBands(d, paceFilterKeys));
  const gradeFilteredData = paceFilteredData.filter((d) => lapMatchesGradeBands(d, gradeFilterKeys));

  const filteredRaw = gradeFilteredData.filter(d => d[activeKpi] != null);
  const outlierFiltered = ROLLING_RANGE_KEYS.has(range)
    ? filteredRaw
    : filterOutliersWithFallback(filteredRaw, activeKpi);
  const filteredData = isThisRun
    ? [...outlierFiltered].sort((a, b) => (a.elapsedSec ?? 0) - (b.elapsedSec ?? 0))
    : outlierFiltered;
  const availableKpis = KPI_DEFS.filter(k => gradeFilteredData.some(d => d[k.key] != null));

  const trend = computeTrend(filteredData, activeKpi);
  const chartData = trend.data;

  /** "This run": point at t=0 uses same KPI/trend as first bucket, or (if series already starts ~0) as sample nearest windowSec — flat segment to the axis. */
  const runChartData =
    isThisRun && chartData.length > 0
      ? (() => {
          const win = runSeriesMeta?.windowSeconds ?? 30;
          const first = chartData[0];
          const lead = Number(first.elapsedSec);
          const nearestWin =
            chartData.reduce((best, d) => {
              const e = Number(d.elapsedSec);
              if (!Number.isFinite(e)) return best;
              if (!best) return d;
              const db = Math.abs(Number(best.elapsedSec) - win);
              const de = Math.abs(e - win);
              return de < db ? d : best;
            }, null) || first;

          const template =
            Number.isFinite(lead) && lead > 0.001 ? first : nearestWin;
          const head = { ...template, elapsedSec: 0 };

          if (Number.isFinite(lead) && lead > 0.001) {
            return [head, ...chartData];
          }

          const rest = chartData.filter((d) => Number(d.elapsedSec) > 0.001);
          if (rest.length === 0) return [head];
          return [head, ...rest];
        })()
      : chartData;

  const chartDurationSec = isThisRun
    ? Math.max(
        runSeriesMeta?.durationSec || 0,
        runChartData.reduce((m, d) => Math.max(m, Number(d.elapsedSec) || 0), 0)
      )
    : null;

  /** Explicit ticks avoid duplicate React keys when Recharts collides tick positions or categories. */
  const xAxisTicks =
    isThisRun && chartDurationSec != null && chartDurationSec > 0
      ? Array.from({ length: 6 }, (_, i) => (chartDurationSec * i) / 5)
      : !isThisRun
        ? downsampleSortedUnique(
            [...new Set(chartData.map((d) => d.date).filter(Boolean))].sort(
              (a, b) => new Date(a) - new Date(b)
            ),
            12
          )
        : undefined;

  const vals = filteredData.map(d => d[activeKpi]);
  const avg = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  const activityCardKpi = activityKpiFromCard && typeof activityKpiFromCard === 'object'
    ? activityKpiFromCard[activeKpi]
    : null;
  // Card KPI line is an overlay on date-range (period) charts only; "This run" is the stream chart alone.
  const showActivityCardLine = !isThisRun && Number.isFinite(activityCardKpi);

  const periodTrendInfo =
    ROLLING_RANGE_KEYS.has(range) && gradeIsListOnlyAll && paceIsSingleForApi && !periodAvgsLoading
      ? getPeriodTrendInfo(periodAvgs.current[activeKpi], periodAvgs.prior[activeKpi], kpiDef)
      : null;

  const trendGoingUp = trend.pctChange > 0;
  const isPositiveRegression = kpiDef.higherIsBetter ? trendGoingUp : !trendGoingUp;
  const regressionTrendColor =
    Math.abs(trend.pctChange) < 0.5 ? '#a1a1aa' : isPositiveRegression ? '#22c55e' : '#ef4444';

  const listAlignedBadge = periodTrendInfo != null;
  const badgeNeutral = listAlignedBadge
    ? periodTrendInfo.isNeutral
    : Math.abs(trend.pctChange) < 0.5;
  const badgeUp = listAlignedBadge ? periodTrendInfo.pctChange > 0 : trendGoingUp;
  const displayBadgePct = listAlignedBadge ? periodTrendInfo.pctChange : trend.pctChange;
  const badgeColor = listAlignedBadge
    ? badgeNeutral
      ? '#a1a1aa'
      : periodTrendInfo.isImprovement
        ? '#22c55e'
        : '#ef4444'
    : regressionTrendColor;

  const regressionLineColor = listAlignedBadge ? 'hsl(var(--muted-foreground))' : regressionTrendColor;

  const showPeriodBadgeLoading =
    !isThisRun &&
    ROLLING_RANGE_KEYS.has(range) &&
    gradeIsListOnlyAll &&
    paceIsSingleForApi &&
    periodAvgsLoading;
  const showTrendBadge =
    !loading &&
    !showPeriodBadgeLoading &&
    (listAlignedBadge || filteredData.length >= 2);

  const chartEmptyHint = (() => {
    if (isThisRun && runSeriesMeta?.missingStreams) {
      return runSeriesMeta.streamsUnavailable
        ? t('kpiTrend.streamsUnavailable')
        : t('kpiTrend.syncStreamsHint');
    }
    return t('kpiTrend.noData');
  })();

  const showElevationBackdrop = isThisRun && runChartData.some(
    (d) => d.altitudeM != null && Number.isFinite(Number(d.altitudeM))
  );
  const chartKpiYAxisId = 'kpi';
  const chartElevYAxisId = 'elev';

  const racesPlaced = useMemo(
    () =>
      isOpen && !isThisRun && runChartData.length > 0
        ? placeRacesOnChartCategories(raceRuns, runChartData.map((d) => d.date))
        : [],
    [isOpen, isThisRun, raceRuns, runChartData]
  );
  const raceStripActive =
    showRaceStrip && racesPlaced.length > 0 && !isThisRun && filteredData.length > 0 && !loading;
  const chartRaceTopMargin = raceStripActive ? 36 : 8;

  if (!isOpen) return null;

  const portal = createPortal(
    <div
      ref={overlayRef}
      className='fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200'
      style={{ zIndex: 10100 }}
      onClick={(e) => { if (e.target === overlayRef.current) handleClose(); }}
    >
      <div
        className='bg-card border border-border rounded-xl shadow-2xl flex min-h-0 min-w-0 max-h-[85vh] flex-col overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300'
        style={{ width: '80vw' }}
      >
        {/* Header */}
        <div className='flex shrink-0 items-center justify-between px-5 py-4 border-b border-border'>
          <div className='flex items-center gap-3'>
            <div>
              <h2 className='text-base font-semibold text-foreground'>{t('kpiTrend.title')}</h2>
              <p className='text-xs text-muted-foreground mt-0.5'>
                {loading && isThisRun ? (
                  <span className='opacity-50'>…</span>
                ) : isThisRun && runSeriesMeta ? (
                  runSeriesMeta.missingStreams ? (
                    runSeriesMeta.durationSec > 0
                      ? `${formatElapsedSeconds(runSeriesMeta.durationSec)} · ${t('kpiTrend.thisRun')}`
                      : t('kpiTrend.thisRun')
                  ) : (
                    t('kpiTrend.thisRunSubtitle', {
                      duration: formatElapsedSeconds(runSeriesMeta.durationSec),
                      count: data.length,
                      sec: runSeriesMeta.windowSeconds,
                    })
                  )
                ) : (
                  <>
                    {filteredData.length} {t('kpiTrend.laps')}
                  </>
                )}
              </p>
            </div>
            {showPeriodBadgeLoading && (
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
            )}
            {showTrendBadge && (
              <div
                className='flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold'
                style={{ backgroundColor: `${badgeColor}18`, color: badgeColor }}
                title={
                  listAlignedBadge
                    ? t('kpiTrend.badgeTooltipList')
                    : t('kpiTrend.badgeTooltipRegression')
                }
              >
                {badgeNeutral
                  ? <Minus className='h-3.5 w-3.5' />
                  : badgeUp
                    ? <TrendingUp className='h-3.5 w-3.5' />
                    : <TrendingDown className='h-3.5 w-3.5' />}
                {Math.abs(displayBadgePct).toFixed(1)}%
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className='p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Date range pills */}
        <div className='flex shrink-0 items-center gap-1.5 px-5 py-2.5 border-b border-border overflow-x-auto'>
          {showThisRun && (
            <button
              type='button'
              onClick={() => updateRange('thisRun')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                range === 'thisRun'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/60 text-foreground/70 hover:text-foreground hover:bg-muted'
              }`}
            >
              {t('kpiTrend.thisRun')}
            </button>
          )}
          {RANGES.map(r => (
            <button
              key={r.key}
              type='button'
              onClick={() => updateRange(r.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                range === r.key
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/60 text-foreground/70 hover:text-foreground hover:bg-muted'
              }`}
            >
              {t(r.labelKey)}
            </button>
          ))}
          {!isThisRun && chartData.length > 0 && (
            <button
              type='button'
              onClick={() => setShowRaceStrip((v) => !v)}
              className={cn(
                'ml-auto px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap',
                showRaceStrip
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/60 text-foreground/70 hover:text-foreground hover:bg-muted'
              )}
              title={t('acwr.raceStripHint')}
            >
              {t('acwr.raceStripToggle')}
            </button>
          )}
        </div>

        {/* KPI toggle */}
        <div className='shrink-0 border-b border-border bg-muted/30 px-5 py-2.5'>
          <div className='flex flex-wrap items-center gap-x-1.5 gap-y-2 overflow-x-auto'>
            {availableKpis.map(k => (
              <button
                key={k.key}
                type='button'
                onClick={() => updateActiveKpi(k.key)}
                className={`inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-medium leading-tight transition-all whitespace-nowrap shrink-0 ${
                  activeKpi === k.key
                    ? 'text-white shadow-sm ring-1 ring-black/10 dark:ring-white/10'
                    : 'text-foreground/85 hover:text-foreground bg-card/80 border-border hover:border-foreground/25 hover:bg-muted/50'
                }`}
                style={activeKpi === k.key ? { backgroundColor: k.color, borderColor: k.color } : {}}
              >
                {t(k.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className='flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-border px-5 py-2'>
          <span className='mr-1 shrink-0 whitespace-nowrap text-xs font-medium text-muted-foreground'>/km</span>
          {PACE_SEGMENTS.map(p => (
            <button
              key={p.key}
              type='button'
              title={t('kpiTrend.shiftMultiSelectInfo')}
              onClick={(e) => handlePaceFilterClick(p.key, e)}
              className={`inline-flex shrink-0 items-center justify-center rounded-md px-2.5 py-1 text-xs font-medium leading-tight transition-all whitespace-nowrap ${
                paceFilterKeys.includes(p.key)
                  ? 'bg-foreground/15 text-foreground shadow-sm ring-1 ring-foreground/20'
                  : 'text-foreground/60 hover:bg-muted/50 hover:text-foreground'
              }`}
            >
              {p.key === 'all' ? t('kpiTrend.allPaces') : p.label}
            </button>
          ))}
        </div>

        <div className='flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-border px-5 py-2'>
          <span className='mr-1 shrink-0 whitespace-nowrap text-xs font-medium text-muted-foreground'>{t('kpiTrend.grade')}:</span>
          {GRADE_SEGMENTS.map(g => (
            <button
              key={g.key}
              type='button'
              title={t('kpiTrend.shiftMultiSelectInfo')}
              onClick={(e) => handleGradeFilterClick(g.key, e)}
              className={`inline-flex shrink-0 items-center justify-center rounded-md px-2.5 py-1 text-xs font-medium leading-tight transition-all whitespace-nowrap ${
                gradeFilterKeys.includes(g.key)
                  ? 'bg-foreground/15 text-foreground shadow-sm ring-1 ring-foreground/20'
                  : 'text-foreground/60 hover:bg-muted/50 hover:text-foreground'
              }`}
            >
              {t(g.labelKey)}
            </button>
          ))}
        </div>

        {!isThisRun && (
          <div className='shrink-0 border-b border-border bg-muted/20 px-5 py-2'>
            <p className='text-[11px] text-muted-foreground leading-snug'>
              {ROLLING_RANGE_KEYS.has(range) && gradeIsListOnlyAll && paceIsSingleForApi && t('kpiTrend.hintRollingListMatch', {
                n: parseInt(String(range).replace('d', ''), 10) || 28,
              })}
              {ROLLING_RANGE_KEYS.has(range) && !(gradeIsListOnlyAll && paceIsSingleForApi) && t('kpiTrend.hintRollingRegressionOnly')}
              {!ROLLING_RANGE_KEYS.has(range) && t('kpiTrend.hintCalendarFit')}
            </p>
          </div>
        )}

        {/* Chart — fixed height so filters/footer do not shift when series or selection changes */}
        <div className='shrink-0 px-5 pt-3 pb-4'>
          <div
            ref={chartHostRef}
            className='min-h-0 w-full min-w-0 shrink-0 overflow-hidden'
            style={{ height: CHART_FIXED_HEIGHT }}
          >
          {loading ? (
            <div className='flex h-full items-center justify-center'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : filteredData.length === 0 ? (
            <div className='flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground'>
              {chartEmptyHint}
            </div>
          ) : chartWidth < 2 ? (
            <div className='flex h-full items-center justify-center'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : (
            <ResponsiveContainer width={chartWidth} height={CHART_FIXED_HEIGHT} debounce={0}>
              <ComposedChart
                key={`${animKey}-${layoutNonce}`}
                data={runChartData}
                margin={{ top: chartRaceTopMargin, right: 8, bottom: 8, left: 4 }}
              >
                <defs>
                  <linearGradient id={`grad-${kpiDef.key}`} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor={kpiDef.color} stopOpacity={0.35} />
                    <stop offset='95%' stopColor={kpiDef.color} stopOpacity={0.02} />
                  </linearGradient>
                  {showElevationBackdrop && (
                    <linearGradient id={`elev-bg-${animKey}`} x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor='hsl(var(--muted-foreground))' stopOpacity={0.2} />
                      <stop offset='100%' stopColor='hsl(var(--muted-foreground))' stopOpacity={0.03} />
                    </linearGradient>
                  )}
                </defs>
                <CartesianGrid strokeDasharray='3 3' stroke='hsl(var(--border))' strokeOpacity={0.5} />
                <XAxis
                  dataKey={isThisRun ? 'elapsedSec' : 'date'}
                  type={isThisRun ? 'number' : 'category'}
                  scale={isThisRun ? 'linear' : undefined}
                  padding={isThisRun ? { left: 0, right: 0 } : undefined}
                  ticks={xAxisTicks && xAxisTicks.length > 0 ? xAxisTicks : undefined}
                  tickFormatter={(v) => {
                    if (isThisRun) return formatElapsedSeconds(v);
                    const d = new Date(v);
                    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                  minTickGap={isThisRun ? 24 : 40}
                  domain={
                    isThisRun && chartDurationSec > 0
                      ? [0, chartDurationSec]
                      : isThisRun
                        ? [0, 'auto']
                        : undefined
                  }
                  allowDataOverflow={isThisRun}
                />
                <YAxis
                  yAxisId={chartKpiYAxisId}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                  domain={['auto', 'auto']}
                />
                {showElevationBackdrop && (
                  <YAxis
                    yAxisId={chartElevYAxisId}
                    orientation='right'
                    domain={['auto', 'auto']}
                    hide
                    width={0}
                  />
                )}
                {showElevationBackdrop && (
                  <Area
                    yAxisId={chartElevYAxisId}
                    type={isThisRun ? 'linear' : 'monotone'}
                    dataKey='altitudeM'
                    stroke='hsl(var(--muted-foreground))'
                    strokeWidth={1}
                    strokeOpacity={0.35}
                    fill={`url(#elev-bg-${animKey})`}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                )}
                <RechartsTooltip
                  content={(props) => {
                    const isCurrent = currentActivityId != null && props.payload?.[0]?.payload && Number(props.payload[0].payload.activityId) === Number(currentActivityId);
                    return (
                      <CustomTooltip
                        {...props}
                        kpiDef={kpiDef}
                        t={t}
                        isCurrentActivity={isCurrent}
                        isDurationAxis={isThisRun}
                      />
                    );
                  }}
                  cursor={{ stroke: kpiDef.color, strokeOpacity: 0.3, strokeDasharray: '4 4' }}
                />
                <ReferenceLine
                  yAxisId={chartKpiYAxisId}
                  y={avg}
                  stroke={kpiDef.color}
                  strokeDasharray='6 4'
                  strokeOpacity={0.4}
                  label={{
                    value: `${showActivityCardLine ? t('kpiTrend.periodAvg') : t('kpiTrend.avg')} ${avg.toFixed(kpiDef.decimals)}`,
                    position: 'right',
                    fill: kpiDef.color,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
                {showActivityCardLine && (
                  <ReferenceLine
                    yAxisId={chartKpiYAxisId}
                    y={activityCardKpi}
                    stroke='hsl(var(--primary))'
                    strokeDasharray='3 3'
                    strokeOpacity={0.9}
                    label={{
                      value: `${t('kpiTrend.activityCardKpi')} ${activityCardKpi.toFixed(kpiDef.decimals)}`,
                      position: 'left',
                      fill: 'hsl(var(--primary))',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />
                )}
                <Area
                  yAxisId={chartKpiYAxisId}
                  type={isThisRun ? 'linear' : 'monotone'}
                  dataKey={kpiDef.key}
                  stroke={kpiDef.color}
                  strokeWidth={2}
                  fill={`url(#grad-${kpiDef.key})`}
                  connectNulls={false}
                  dot={
                    isThisRun
                      ? false
                      : (props) => {
                          const { key: dotKey, ...dotRest } = props;
                          const isCurrent = currentActivityId != null && Number(dotRest.payload?.activityId) === Number(currentActivityId);
                          return (
                            <CustomDot
                              key={dotKey}
                              {...dotRest}
                              kpiKey={kpiDef.key}
                              color={kpiDef.color}
                              isCurrentActivity={isCurrent}
                              dotClickable={periodDotClickable}
                              onDotActivityClick={setPeekActivityId}
                            />
                          );
                        }
                  }
                  activeDot={
                    isThisRun
                      ? { r: 5, strokeWidth: 2, stroke: 'hsl(var(--background))', fill: kpiDef.color }
                      : (props) => {
                          const { key: activeKey, ...activeRest } = props;
                          const isCurrent = currentActivityId != null && Number(activeRest.payload?.activityId) === Number(currentActivityId);
                          return (
                            <CustomActiveDot
                              key={activeKey}
                              {...activeRest}
                              kpiKey={kpiDef.key}
                              color={kpiDef.color}
                              isCurrentActivity={isCurrent}
                              dotClickable={periodDotClickable}
                              onDotActivityClick={setPeekActivityId}
                            />
                          );
                        }
                  }
                  isAnimationActive={!isThisRun}
                  animationDuration={1200}
                  animationEasing='ease-out'
                />
                {/* Trend line – white stroke behind for contrast */}
                <Line
                  yAxisId={chartKpiYAxisId}
                  type={isThisRun ? 'linear' : 'monotone'}
                  dataKey='_trend'
                  stroke='hsl(var(--background))'
                  strokeWidth={5}
                  strokeDasharray='10 5'
                  strokeOpacity={0.9}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId={chartKpiYAxisId}
                  type={isThisRun ? 'linear' : 'monotone'}
                  dataKey='_trend'
                  stroke={regressionLineColor}
                  strokeWidth={3}
                  strokeDasharray='10 5'
                  strokeOpacity={0.95}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={!isThisRun}
                  animationDuration={1600}
                  animationEasing='ease-out'
                />
                {raceStripActive && (
                  <Customized
                    component={(chartProps) => (
                      <AcwrRaceStripLayer
                        {...chartProps}
                        races={racesPlaced}
                        xDataKey='chartX'
                        visible={raceStripActive}
                        onHover={setRaceTip}
                        onLeave={() => setRaceTip(null)}
                      />
                    )}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
          </div>
        </div>
        {!isThisRun && filteredData.length > 0 && !loading && (
          <p className='shrink-0 px-5 pb-2 text-center text-[11px] text-muted-foreground'>
            {t('kpiTrend.clickDotForActivity')}
          </p>
        )}

        {/* Footer stats */}
        {filteredData.length > 0 && !loading && (
          <div className='flex shrink-0 items-center justify-between border-t border-border px-5 py-3'>
            <div className='flex items-center gap-4'>
              {[
                { label: showActivityCardLine ? t('kpiTrend.periodAvg') : t('kpiTrend.avg'), value: avg.toFixed(kpiDef.decimals) },
                { label: t('kpiTrend.min'), value: Math.min(...vals).toFixed(kpiDef.decimals) },
                { label: t('kpiTrend.max'), value: Math.max(...vals).toFixed(kpiDef.decimals) },
              ].map(s => (
                <div key={s.label} className='flex items-center gap-1.5'>
                  <span className='text-xs text-muted-foreground'>{s.label}</span>
                  <span className='text-sm font-semibold' style={{ color: kpiDef.color }}>{s.value}</span>
                  {kpiDef.unit && <span className='text-xs text-muted-foreground'>{kpiDef.unit}</span>}
                </div>
              ))}
            </div>
            {(filteredData.length >= 2 || periodTrendInfo) && (
              <div className='flex flex-col items-end gap-1.5 text-xs text-right max-w-[min(100%,320px)]'>
                {periodTrendInfo && (
                  <div className='flex flex-wrap items-center justify-end gap-1.5'>
                    <span className='text-muted-foreground'>{t('kpiTrend.vsPriorPeriod')}:</span>
                    <span style={{ color: badgeColor }} className='font-semibold'>
                      {periodTrendInfo.pctChange > 0 ? '↗' : periodTrendInfo.pctChange < 0 ? '↘' : '—'}
                      {' '}
                      {Math.abs(periodTrendInfo.pctChange).toFixed(1)}%
                    </span>
                  </div>
                )}
                {filteredData.length >= 2 && (
                  <div className='flex flex-wrap items-center justify-end gap-1.5'>
                    <span className='text-muted-foreground'>{t('kpiTrend.lapOrderFit')}:</span>
                    <span style={{ color: regressionLineColor }} className='font-medium'>
                      {trend.startVal.toFixed(kpiDef.decimals)} → {trend.endVal.toFixed(kpiDef.decimals)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {raceStripActive && (
          <div className='shrink-0 px-5 py-2 border-t border-border flex flex-wrap items-center gap-4 text-xs text-muted-foreground'>
            <span className='flex items-center gap-1.5'>
              <span className='w-2 h-2 rounded-full bg-primary border border-border shrink-0' />
              {t('acwr.raceStripLegend')}
            </span>
          </div>
        )}
        <RaceStripHoverCard
          race={raceTip?.race}
          clientX={raceTip?.clientX}
          clientY={raceTip?.clientY}
          t={t}
        />
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {portal}
      <ActivityPeekModal activityId={peekActivityId} onClose={() => setPeekActivityId(null)} />
    </>
  );
}
